<template>
  <div class="form-input-element-wrapper">
    <FormLabel
      :element="element"
      :labelStyle="labelStyle"
      :fieldStyle="fieldStyle"
    />
    <FormDescription :element="element" :labelStyle="labelStyle" />

    <div
      class="input-field-wrapper position-relative"
      :class="{ 'disabled-wrapper': isDisabled }"
    >
      <input
        :style="fieldStyle"
        class="form-input fw-normal fs-6 w-100 bg-transparent"
        :class="{ 'user-select-none': isDisabled }"
        :id="`input-${element.id}`"
        :name="element.name"
        :value="inputValue"
        :type="element.type"
        :step="getInputStep"
        :required="isRequired"
        :disabled="isDisabled"
        :pattern="getPattern"
        :min="getElementMinMax('min')"
        :max="getElementMinMax('max')"
        :placeholder="getPlaceholder"
        @input="onHandleInput"
        @change="onHandleChange"
        @invalid="onHandleInvalid"
      />
      <div
        v-if="element?.settings?.prefixIcon?.icon"
        class="prefix-image-wrap position-absolute overflow-hidden bg-transparent d-flex align-items-center justify-content-center"
      >
        <img
          :id="`prefix-icon-${element.id}`"
          class="prefix-icon object-fit-contain mh-100 mw-100"
          :src="element.settings.prefixIcon.icon"
        />
      </div>
      <img
        class="error-icon position-absolute top-50"
        :src="require('@/assets/img/error-circle-exclamation.svg')"
      />
    </div>
  </div>
</template>

<script>
import { DEFAULT_FORM_STYLES } from '@/utils/constants/WorkFlowConstants';
import { showInputInvalidCustomMessage } from '@/utils/helpers';

import FormLabel from '@/components/FormBuilder/core/FormLabel.vue';
import FormDescription from '@/components/FormBuilder/core/FormDescription.vue';
export default {
  name: 'FormInputElement',
  props: {
    element: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    labelStyle: { type: Object, default: () => {} },
    fieldStyle: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
  },
  methods: {
    onHandleInput(event) {
      this.onHandleInvalid(event);
      const inputElement = event.target;
      if (this.element.type === 'file') {
        return;
      }

      this.inputValue = inputElement.value;
    },
    onHandleChange(event) {
      if (this.element.type === 'file') {
        this.element.files = event.target.files;
      }
    },
    onHandleInvalid(event) {
      if (this.getPattern && this.element.settings?.errorMessage) {
        if (event.target?.validity?.patternMismatch) {
          event.target.setCustomValidity(this.element.settings?.errorMessage);
          return;
        }
      }

      showInputInvalidCustomMessage(event);
    },

    getElementMinMax(type) {
      if (
        ['range', 'number'].includes(this.element.type) &&
        this.element.settings.hasOwnProperty(type) &&
        this.element.settings[type]
      ) {
        return Number(this.element.settings[type]);
      }
      return null;
    },
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    getPlaceholder() {
      return this.element.settings.placeholder || '';
    },
    getPattern() {
      if (this.element.type === 'text' && this.element?.settings?.pattern) {
        return `${this.element.settings.pattern}`;
      }
      return null;
    },
    getInputStep() {
      if (this.element.type !== 'number') {
        return null;
      }
      const configuredStep = this.element?.settings?.step;
      if (
        configuredStep === null ||
        configuredStep === undefined ||
        configuredStep === ''
      ) {
        return 'any';
      }
      const parsedStep = Number(configuredStep);
      return Number.isFinite(parsedStep) && parsedStep > 0 ? parsedStep : 'any';
    },
    isRequired() {
      return this.element.settings.required || false;
    },
    isDisabled() {
      return this.disabled || this.element.settings.disabled;
    },
    placeholderStyle() {
      return {
        color:
          this.fieldStyle?.placeholderColor ||
          DEFAULT_FORM_STYLES.placeHolderColor,
        fontFamily:
          this.fieldStyle.fontFamily || DEFAULT_FORM_STYLES.placeholderFont,
      };
    },
    inputTextColor() {
      return this.fieldStyle?.textColor || DEFAULT_FORM_STYLES.inputColor;
    },
  },
  components: {
    FormLabel,
    FormDescription,
  },
};
</script>

<style scoped>
.form-input-element-wrapper:has(input[type='hidden']) .form-label,
.form-input-element-wrapper:has(.form-description) .form-label {
  margin-bottom: 9px;
}

.form-input-element-wrapper
  label:hover
  ~ .input-field-wrapper
  input[type='number']::-webkit-inner-spin-button,
.form-input-element-wrapper
  label:hover
  ~ .input-field-wrapper
  input[type='number']::-webkit-outer-spin-button {
  opacity: 0 !important;
}

.input-field-wrapper.disabled-wrapper {
  background: #fbfbfb;
}

.form-input-element-wrapper .form-input {
  height: 65px;
  font-family: 'OpenSans-Regular';
  border: 0.7px solid #dddddd;
  border-radius: 10px;
  padding: 0 23px;
  line-height: 20px;
  letter-spacing: -0.02em;
  color: #222222;
  cursor: auto;
  vertical-align: middle;
}

.form-input-element-wrapper .form-input::placeholder {
  font-weight: 600;
  font-family: v-bind('placeholderStyle.fontFamily');
  color: v-bind('placeholderStyle.color');
}

.form-input-element-wrapper input:focus::placeholder {
  color: transparent !important;
}

.form-input-element-wrapper input:-webkit-autofill {
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: v-bind(inputTextColor);
}
.form-input-element-wrapper input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  color: #c5c5c5 !important;
  border-color: #ededed !important;
}
.form-input-element-wrapper input:focus {
  box-shadow: none;
  border-color: transparent !important;
  outline: 2.2px solid #000000;
  outline-offset: -2.2px;
}
.form-input-element-wrapper input[type='file'] {
  padding: 16px 23px !important;
  line-height: 27px !important;
}

.form-input-element-wrapper .input-field-wrapper img.error-icon {
  right: 25px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ff0000;
  width: 21px;
  height: 21px;
  display: none;
  user-drag: none;
  -webkit-user-drag: none;
}
.form-input-element-wrapper .input-field-wrapper .prefix-image-wrap {
  width: 30px;
  height: 30px;
  left: 15px;
  top: 17.5px;
}

.form-input-element-wrapper .prefix-image-wrap img.prefix-icon {
  -webkit-user-drag: none;
}
.form-input-element-wrapper:has(.prefix-icon) .form-input {
  padding-left: 60px !important;
}
.was-submitted
  .form-input-element-wrapper:has(.form-input:invalid)
  img.error-icon {
  display: block;
}

.was-submitted
  .form-input-element-wrapper
  input[type='time']:invalid::-webkit-calendar-picker-indicator,
.was-submitted
  .form-input-element-wrapper
  input[type='datetime-local']:invalid::-webkit-calendar-picker-indicator,
.was-submitted
  .form-input-element-wrapper
  input[type='date']:invalid::-webkit-calendar-picker-indicator {
  padding-right: 30px;
}
.was-submitted
  .form-input-element-wrapper
  input[type='number']:invalid::-webkit-inner-spin-button,
.was-submitted
  .form-input-element-wrapper
  input[type='number']:invalid::-webkit-outer-spin-button {
  transform: translateX(-30px);
}
.was-submitted .form-input-element-wrapper .form-input:invalid {
  outline: 2.2px solid #ff0000 !important;
  background-color: #ff000008;
  border-color: transparent !important;
  outline-offset: -2.2px;
}
</style>
